import { createAsyncThunk } from "@reduxjs/toolkit";
import { HttpService, ConversationTypeEnum } from "../../modules";
import { ChatListItem, ChatMessage, Conversation, ConversationAttachment } from "../../modules/shared/model/ChatInterfaces";
interface FetchConversationsArgs {
  chatId: string;
  page?: number;
  limit?: number;
  isShowLoader?: boolean;
}

interface FetchConversationsResponse {
  data: Conversation[];
  pagination: {
    page: number;
    limit: number;
    hasNextPage: boolean;
    hasPreviousPage: boolean;
    totalCount: number | null;
  };
}
import { hideLoader, showLoader } from "../Slices/loadingSlice";
import { webSocketService } from "../../services/websocketService";

// Upload file for chat attachments
export const uploadChatFile = createAsyncThunk<any, { file: File; shouldShowLoader?: boolean }>(
  "chat/uploadChatFile",
  async ({ file, shouldShowLoader = false }, { dispatch, rejectWithValue }) => {
    try {
      if (shouldShowLoader) {
        dispatch(showLoader());
      }

      const formData = new FormData();
      formData.append("file", file);

      const response = await HttpService.post(
        "api/v1/files/upload",
        formData,
        { "Content-Type": "multipart/form-data" },
        true
      );

      if (shouldShowLoader) {
        dispatch(hideLoader());
      }

      // Add original file information to the response
      return {
        ...response,
        file: {
          ...response.file,
          name: file.name,
          type: file.type
        }
      };
    } catch (error: any) {
      if (shouldShowLoader) {
        dispatch(hideLoader());
      }

      const errorMessage = error?.data?.message || "Failed to upload file";
      return rejectWithValue(errorMessage);
    }
  }
);

// Fetch chats for a participant
export const fetchParticipantChats = createAsyncThunk<any, { currentUserId: string; page?: number; limit?: number }>(
  "chat/fetchParticipantChats",
  async ({ currentUserId, page = 1, limit = 10 }, { dispatch, rejectWithValue }) => {
    try {
      dispatch(showLoader());
      
      // Build query parameters
      const params = new URLSearchParams();
      params.append("page", page.toString());
      params.append("limit", limit.toString());
      
      const response = await HttpService.get(`api/v1/chats/participant/${currentUserId}?${params.toString()}`);
      dispatch(hideLoader());

      // Transform the API response to match our ChatListItem interface
      const transformedChats = response.data.map((chat: any) => ({
        id: chat.contextId,
        name: chat.participants.find((p: any) => p.id !== currentUserId)?.firstName || "Unknown User",
        avatar: chat.participants.find((p: any) => p.id !== currentUserId)?.photo?.path || null,
        lastMessage: chat.lastMessage || "No messages yet",
        timestamp: formatTimestamp(chat.lastMessageAt),
        unread: chat.unreadCount?.[currentUserId] || 0,
        status: "online", // This would need to come from a separate API
        pinned: false, // This would need to come from user preferences
        chat: chat
      }));

      return {
        chats: transformedChats,
        pagination: {
          currentPage: page,
          totalCount: response.totalCount || 0,
          hasNextPage: response.hasNextPage || false,
          hasPreviousPage: page > 1
        }
      };
    } catch (error: any) {
      dispatch(hideLoader());
      const errorMessage = error?.data?.message || "Failed to fetch chats";
      return rejectWithValue(errorMessage);
    }
  }
);

// Fetch messages for a specific chat
export const fetchChatMessages = createAsyncThunk<ChatMessage[], { chatId: string, currentUserId: string }>(
  "chat/fetchChatMessages",
  async ({ chatId, currentUserId }, { dispatch, rejectWithValue }) => {
    try {
      dispatch(showLoader());
      const response = await HttpService.get(`api/v1/chats/${chatId}/messages`);
      dispatch(hideLoader());

      // Transform the API response to match our ChatMessage interface
      const transformedMessages = response.map((msg: any) => ({
        id: msg.id,
        sender: msg.senderId === currentUserId ? "Me" : msg.senderName || "Unknown",
        content: msg.content,
        timestamp: formatTimestamp(msg.createdAt),
        status: msg.status || "sent",
        isMine: msg.senderId === currentUserId
      }));

      return transformedMessages;
    } catch (error: any) {
      dispatch(hideLoader());
      const errorMessage = error?.data?.message || "Failed to fetch messages";
      return rejectWithValue(errorMessage);
    }
  }
);

// Send a message
export const sendMessage = createAsyncThunk<ChatMessage, { chatId: string, content: string, currentUserId: string }>(
  "chat/sendMessage",
  async ({ chatId, content, currentUserId }, { dispatch, rejectWithValue }) => {
    try {
      const messageData = {
        content,
        senderId: currentUserId,
        chatId
      };

      const response = await HttpService.post(`api/v1/chats/${chatId}/messages`, messageData);

      // Transform the response to match our ChatMessage interface
      const transformedMessage: ChatMessage = {
        id: response.id,
        sender: "Me",
        content: response.content,
        timestamp: formatTimestamp(response.createdAt),
        status: "sent",
        isMine: true
      };

      return transformedMessage;
    } catch (error: any) {
      const errorMessage = error?.data?.message || "Failed to send message";
      return rejectWithValue(errorMessage);
    }
  }
);

// Send a conversation message
export const sendConversation = createAsyncThunk<Conversation, {
  message: string,
  chatId: string,
  currentUserId: string,
  attachments?: ConversationAttachment[],
  type?: number,
  referenceId?: string,
  chatParticipants?: string[],
  replyTo?: {
    id: string;
  },
  isReply?: boolean
}>(
  "chat/sendConversation",
  async ({ message, chatId, currentUserId, attachments = [], type = ConversationTypeEnum.Text, referenceId, chatParticipants, replyTo, isReply = false }, { dispatch, rejectWithValue }) => {
    try {
      const conversationData = {
        isRead: true,
        attachments,
        message,
        sender: {
          id: currentUserId
        },
        chatId: {
          id: chatId
        },
        type: { id: type || ConversationTypeEnum.Text },
        ...(referenceId ? { referenceId } : {}),
        ...(replyTo ? { replyTo } : {}),
        isReply: isReply
      };

      console.log("📤 Sending conversation data:", conversationData);
      const response = await HttpService.post("api/v1/conversations", conversationData);
      console.log("✅ Conversation created successfully:", response);

      // Send WebSocket message to notify other participants (non-blocking)
      if (chatParticipants && chatParticipants.length > 0) {
        const receivers = chatParticipants.filter(id => id !== currentUserId);

        if (receivers.length > 0) {
          // Use setTimeout to make WebSocket call non-blocking
          setTimeout(async () => {
            try {
              if (webSocketService.getConnectionStatus()) {
                await webSocketService.sendMessage({
                  receivers,
                  sender: currentUserId,
                  type: "new_conversation",
                  detail: {
                    conversationId: response.id,
                    conversation: response,
                    chatId: chatId,
                    message: message,
                    senderId: currentUserId,
                    attachments: attachments,
                    type: type || ConversationTypeEnum.Text,
                    referenceId: referenceId,
                    timestamp: new Date().toISOString()
                  },
                  conversationId: response.id
                });

                console.log("📤 WebSocket message sent for new conversation");
              } else {
                console.warn("⚠️ WebSocket not connected, skipping notification");
              }
            } catch (wsError) {
              console.warn("⚠️ Failed to send WebSocket message for conversation:", wsError);
              // Don't fail the entire operation if WebSocket fails
            }
          }, 0);
        }
      }

      // Always return the response regardless of WebSocket status
      return response;
    } catch (error: any) {
      console.error("❌ Error sending conversation:", error);
      const errorMessage = error?.data?.message || error?.message || "Failed to send conversation";
      return rejectWithValue(errorMessage);
    }
  }
);

// Fetch conversations for a specific chat
export const fetchConversations = createAsyncThunk<
  FetchConversationsResponse,
  FetchConversationsArgs
>(
  "chat/fetchConversations",
  async (
    { chatId, page = 1, limit = 10, isShowLoader = true },
    { dispatch, rejectWithValue },
  ) => {
    try {
      if (isShowLoader) {
        dispatch(showLoader());
      }

      const params = new URLSearchParams();
      params.append("page", page.toString());
      params.append("limit", limit.toString());

      const response = await HttpService.get(
        `api/v1/conversations/chat/${chatId}?${params.toString()}`,
      );

      if (isShowLoader) {
        dispatch(hideLoader());
      }

      return {
        data: response?.data ?? [],
        pagination: {
          page: response?.page ?? page,
          limit: response?.limit ?? limit,
          hasNextPage: response?.hasNextPage ?? false,
          hasPreviousPage: response?.hasPreviousPage ?? page > 1,
          totalCount: response?.totalCount ?? null,
        },
      };
    } catch (error: any) {
      if (isShowLoader) {
        dispatch(hideLoader());
      }
      const errorMessage =
        error?.data?.message || error?.message || "Failed to fetch conversations";
      return rejectWithValue(errorMessage);
    }
  },
);

// Create a new chat
export const createProfessionalChat = createAsyncThunk<any, {
  currentUserId: string,
  professionalId: string,
  professionalName: string,
  offerId?: string,
  lastMessage?: string
}>(
  "chat/createProfessionalChat",
  async ({ currentUserId, professionalId, professionalName, offerId = null, lastMessage = "" }, { dispatch, rejectWithValue }) => {
    try {
      dispatch(showLoader());

      const chatData = {
        contextType: {
          id: 2 // Message context type
        },
        unreadCount: {},
        participants: [
          {
            id: currentUserId
          },
          {
            id: professionalId
          }
        ],
        contextId: offerId || `${currentUserId}_${professionalId}`,
        lastMessageAt: new Date().toISOString(),
        lastMessage: lastMessage || `Chat started with ${professionalName}`
      };

      const response = await HttpService.post("api/v1/chats", chatData);
      dispatch(hideLoader());

      return response;
    } catch (error: any) {
      dispatch(hideLoader());
      const errorMessage = error?.data?.message || "Failed to create chat";
      return rejectWithValue(errorMessage);
    }
  }
);

// Check if chat exists between two participants
export const checkChatBetweenParticipants = createAsyncThunk<any, {
  userId1: string,
  userId2: string
}>(
  "chat/checkChatBetweenParticipants",
  async ({ userId1, userId2 }, { dispatch, rejectWithValue }) => {
    try {
      const response = await HttpService.get(
        `api/v1/chats/check-between-participants/${userId1}/${userId2}`
      );
      return response;
    } catch (error: any) {
      // If error, it means chat doesn't exist - return null instead of rejecting
      return null;
    }
  }
);

// Admin Chat Management Actions
export const getAllAdminChats = createAsyncThunk<any, {
  page?: number;
  limit?: number;
  isShowLoader?: boolean;
  filters?: { keyword?: string };
  sort?: Array<{ orderBy: string; order: string }>;
}>(
  "chat/getAllAdminChats",
  async ({ page = 1, limit = 10, isShowLoader = true, filters, sort }, { dispatch, rejectWithValue }) => {
    try {
      if (isShowLoader) {
        dispatch(showLoader());
      }

      // Build query parameters
      const queryParams = new URLSearchParams();
      queryParams.append("page", page.toString());
      queryParams.append("limit", limit.toString());

      // Add filters if provided
      if (filters && Object.keys(filters).length > 0) {
        queryParams.append("filters", JSON.stringify(filters));
      }

      // Add sort if provided
      if (sort && sort.length > 0) {
        queryParams.append("sort", JSON.stringify(sort));
      }

      const url = `api/v1/chats?${queryParams.toString()}`;
      console.log("API URL:", url);

      const response = await HttpService.get(url, true, {});

      if (isShowLoader) {
        dispatch(hideLoader());
      }

      return response;
    } catch (error: any) {
      if (isShowLoader) {
        dispatch(hideLoader());
      }

      const errorMessage = error?.data?.message || "Failed to fetch admin chats";
      return rejectWithValue(errorMessage);
    }
  }
);

export const getAdminChatConversations = createAsyncThunk<
  FetchConversationsResponse,
  { chatId: string; page?: number; limit?: number; isShowLoader?: boolean }
>(
  "chat/getAdminChatConversations",
  async (
    { chatId, page = 1, limit = 20, isShowLoader = true },
    { dispatch, rejectWithValue },
  ) => {
    try {
      if (isShowLoader) {
        dispatch(showLoader());
      }

      const params = new URLSearchParams();
      params.append("page", page.toString());
      params.append("limit", limit.toString());

      const response = await HttpService.get(
        `api/v1/conversations/chat/${chatId}?${params.toString()}`,
        true,
        {}
      );

      if (isShowLoader) {
        dispatch(hideLoader());
      }

      return {
        data: response?.data ?? [],
        pagination: {
          page: response?.page ?? page,
          limit: response?.limit ?? limit,
          hasNextPage: response?.hasNextPage ?? false,
          hasPreviousPage: response?.hasPreviousPage ?? page > 1,
          totalCount: response?.totalCount ?? null,
        },
      };
    } catch (error: any) {
      if (isShowLoader) {
        dispatch(hideLoader());
      }

      const errorMessage = error?.data?.message || "Failed to fetch chat conversations";
      return rejectWithValue(errorMessage);
    }
  }
);

export const getAdminChatStatistics = createAsyncThunk<any, { isShowLoader?: boolean }>(
  "chat/getAdminChatStatistics",
  async ({ isShowLoader = false }, { dispatch, rejectWithValue }) => {
    try {
      if (isShowLoader) {
        dispatch(showLoader());
      }

      const response = await HttpService.get(
        "api/v1/chats/admin/statistics",
        true,
        {}
      );

      if (isShowLoader) {
        dispatch(hideLoader());
      }

      return response;
    } catch (error: any) {
      if (isShowLoader) {
        dispatch(hideLoader());
      }

      const errorMessage = error?.data?.message || "Failed to fetch chat statistics";
      return rejectWithValue(errorMessage);
    }
  }
);

export const flagChatMessage = createAsyncThunk<any, { messageId: string; reason: string; isShowLoader?: boolean }>(
  "chat/flagChatMessage",
  async ({ messageId, reason, isShowLoader = true }, { dispatch, rejectWithValue }) => {
    try {
      if (isShowLoader) {
        dispatch(showLoader());
      }

      const response = await HttpService.post(
        `api/v1/conversations/${messageId}/flag`,
        { reason },
        { "Content-Type": "application/json" },
        true
      );

      if (isShowLoader) {
        dispatch(hideLoader());
      }

      return response;
    } catch (error: any) {
      if (isShowLoader) {
        dispatch(hideLoader());
      }

      const errorMessage = error?.data?.message || "Failed to flag message";
      return rejectWithValue(errorMessage);
    }
  }
);

export const blockChatConversation = createAsyncThunk<any, { chatId: string; reason: string; isShowLoader?: boolean }>(
  "chat/blockChatConversation",
  async ({ chatId, reason, isShowLoader = true }, { dispatch, rejectWithValue }) => {
    try {
      if (isShowLoader) {
        dispatch(showLoader());
      }

      const response = await HttpService.post(
        `api/v1/chats/${chatId}/block`,
        { reason },
        { "Content-Type": "application/json" },
        true
      );

      if (isShowLoader) {
        dispatch(hideLoader());
      }

      return response;
    } catch (error: any) {
      if (isShowLoader) {
        dispatch(hideLoader());
      }

      const errorMessage = error?.data?.message || "Failed to block conversation";
      return rejectWithValue(errorMessage);
    }
  }
);

// Update chat action
export const updateChat = createAsyncThunk<any, {
  chatId: string;
  data: {
    contextType?: { id: number };
    status?: { id: number };
    unreadCount?: Record<string, number>;
    participants?: Array<{ id: string }>;
    contextId?: string;
    lastMessageAt?: string;
    lastMessage?: string;
  };
  isShowLoader?: boolean;
}>(
  "chat/updateChat",
  async ({ chatId, data, isShowLoader = true }, { dispatch, rejectWithValue }) => {
    try {
      if (isShowLoader) {
        dispatch(showLoader());
      }
      delete data["conversations"];
      if (data.status) {
        data.status = { id: Number(data.status.id) };
      }
      const response = await HttpService.patch(
        `api/v1/chats/${chatId}`,
        data,
        { "Content-Type": "application/json" },
        true
      );

      if (isShowLoader) {
        dispatch(hideLoader());
      }

      return response;
    } catch (error: any) {
      if (isShowLoader) {
        dispatch(hideLoader());
      }

      const errorMessage = error?.data?.message || "Failed to update chat";
      return rejectWithValue(errorMessage);
    }
  }
);

// Fetch participants by location
export const fetchParticipantsByLocation = createAsyncThunk<any, {
  isShowLoader?: boolean;
}>(
  "chat/fetchParticipantsByLocation",
  async ({ isShowLoader = true }, { dispatch, rejectWithValue }) => {
    try {
      if (isShowLoader) {
        dispatch(showLoader());
      }

      const response = await HttpService.get(
        "api/v1/chats/participants/by-location",
        true,
        {}
      );

      if (isShowLoader) {
        dispatch(hideLoader());
      }

      return response;
    } catch (error: any) {
      if (isShowLoader) {
        dispatch(hideLoader());
      }

      const errorMessage = error?.data?.message || "Failed to fetch participants by location";
      return rejectWithValue(errorMessage);
    }
  }
);

// Helper function to format timestamp
const formatTimestamp = (timestamp: string): string => {
  if (!timestamp) return "";

  const date = new Date(timestamp);
  const now = new Date();
  const diffInHours = (now.getTime() - date.getTime()) / (1000 * 60 * 60);

  if (diffInHours < 24) {
    return date.toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true
    });
  } else if (diffInHours < 48) {
    return "Yesterday";
  } else {
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric"
    });
  }
};
